import { useRouter } from 'expo-router'
import { useEffect, useState, type ReactNode } from 'react'
import { View } from 'react-native'
import { KeyboardAvoidingView } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { Button } from '@/src/elements/button/default'
import { Header } from '@/src/elements/header'
import { Textarea } from '@/src/elements/input/Textarea'
import { Placeholder } from '@/src/elements/placeholder'
import { Typography } from '@/src/elements/text/Typography'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { prefetchRewardCitations } from '@/src/features/pray/services/useRewardCitationsQuery'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useBackHandlePress } from '@/src/general/hooks/useBackHandlePress'
import { useOnChange } from '@/src/general/hooks/useOnChange'
import { GTM } from '@/src/general/utils/gtm'
import { EVENTS } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { useCreateIntentionMutation } from '../../../services/useCreateIntentionMutation'
import { useDeleteIntentionMutation } from '../../../services/useDeleteIntentionMutation'
import { useEditIntentionMutation } from '../../../services/useEditIntentionMutation'
import { CenteredScrollView } from './components/CenteredScrollView'
import { IntentionFormScreenModal } from './components/IntentionFormScreenModal'

const BackArrow = ({ onPress }: { onPress: VoidFunction }): ReactNode => {
  return (
    <Button
      colors="primary"
      contrast="low"
      variant="light"
      isIconOnly
      testID={TEST_ID.INTENTIONS.FORM.SCREEN.BACK}
      startIcon={ICON.ARROW_LEFT}
      onPress={onPress}
    />
  )
}

type TIntentionFormScreenProps = { theme: string | undefined }

export const IntentionFormScreen = ({ theme }: TIntentionFormScreenProps): ReactNode => {
  const [showModal, setShowModal] = useState(false)
  const [intentionContent, setIntentionContent] = useState('')
  const { t } = useTranslate()
  const { routes } = useRoutes()
  const router = useRouter()
  const me = useMeQuery()
  const { bottom } = useSafeAreaInsets()
  const { handleBackPress } = useBackHandlePress(routes.intentions(theme))
  const createIntention = useCreateIntentionMutation({ landingPageSlug: theme })

  const editIntention = useEditIntentionMutation({ content: intentionContent })

  const deleteIntention = useDeleteIntentionMutation(me.data?.intention?.id)

  const savedIntentionContent = me.isLoading ? null : (me.data?.intention?.content ?? '')
  useOnChange(savedIntentionContent, () => {
    if (savedIntentionContent !== null) setIntentionContent(savedIntentionContent)
  })

  const goBackToIntentions = () => {
    if (router.canGoBack()) {
      router.back()
    } else {
      router.navigate(routes.intentions(theme))
    }
  }

  const handleSubmit = () => {
    const intentionId = me.data?.intention?.id
    if (intentionId) {
      editIntention.mutate(intentionId, {
        onSuccess: () => {
          editIntention.reset()
          goBackToIntentions()
        },
      })
    } else {
      createIntention.mutate(intentionContent, {
        onSuccess: () => {
          createIntention.reset()
          router.navigate(routes.intentionCreateReward())
        },
      })
      GTM.sendEvent({ event: EVENTS.INTENTION_ADDED })
    }
  }

  const handleDelete = () => {
    deleteIntention.mutate(undefined, {
      onSuccess: () => {
        deleteIntention.reset()
        goBackToIntentions()
      },
    })
    GTM.sendEvent({ event: EVENTS.INTENTION_DELETED })
    setShowModal(false)
  }

  useEffect(() => {
    prefetchRewardCitations({ type: undefined })
  }, [])

  return (
    <KeyboardAvoidingView
      behavior="padding"
      // We cancel the pb-safe because we want the buttons to be right above the keyboard
      keyboardVerticalOffset={-1 * bottom}
      className="h-full w-full bg-primary-bg-medium"
      testID={TEST_ID.INTENTIONS.FORM.SCREEN.NAME}
    >
      <Header
        leftButtons={[
          {
            type: 'button',
            component: () => <BackArrow onPress={handleBackPress} />,
          },
        ]}
      />
      <View className="mx-auto mb-3 flex-1">
        <CenteredScrollView className="w-full flex-1 self-center px-8 min-h-10">
          {me.isLoading ? (
            <Placeholder
              className="mb-40 h-60 w-full bg-primary-bg-high"
              testID={TEST_ID.INTENTIONS.FORM.SCREEN.LOADING}
            />
          ) : (
            <Textarea
              testID={TEST_ID.INTENTIONS.FORM.SCREEN.TEXTAREA}
              autofocus
              color="primary"
              classNames={{
                inputWrapper: 'bg-transparent px-0 md:w-[400px] mx-auto border-none native:min-w-full native:border-0',
                input:
                  'placeholder:text-default-text-placeholder text-primary-text-contrast-default text-xl md:text-2xl md:leading-10 text-center font-bold content-center',
              }}
              placeholder={t('intention.write.here')}
              numberOfLines={10}
              maxLength={160}
              onChange={(e) => setIntentionContent(e.nativeEvent.text)}
              value={intentionContent}
            />
          )}
        </CenteredScrollView>
        <View className="native:px-4 flex flex-row items-center justify-between gap-4 web:p-6 md:w-[600px] native:pb-safe">
          <Typography colors="primary" contrast="low">
            {t('word.characters', { count: 160 - intentionContent.length })}
          </Typography>
          <View className="flex flex-row gap-4">
            {!!me.data?.intention && (
              <Button
                testID={TEST_ID.INTENTIONS.FORM.SCREEN.DELETE}
                onPress={() => setShowModal(true)}
                colors="danger"
                startIcon={ICON.TRASH}
                variant="light"
                contrast="low"
                isIconOnly
              />
            )}
            <Button
              testID={TEST_ID.INTENTIONS.FORM.SCREEN.SEND}
              onPress={handleSubmit}
              endIcon={ICON.SEND}
              variant="solid"
              colors="primary"
              isDisabled={!intentionContent.length}
            >
              {t(me.data?.intention ? 'word.edit' : 'word.leave-message')}
            </Button>
          </View>
        </View>
      </View>
      <IntentionFormScreenModal isOpen={showModal} onCancel={() => setShowModal(false)} onConfirm={handleDelete} />
    </KeyboardAvoidingView>
  )
}
